/**
 * @fileoverview CoverageEntryForm — Single coverage form card.
 *
 * Unified shape for both imported (pre-filled) and manual (empty) coverages.
 * Same pattern as ProviderEntryForm. Carrier name uses the carrier picker
 * for entity resolution.
 */
import { Loader2, Sparkles, Trash2 } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { toast } from 'sonner';


import { CarrierNameSelect } from '@/components/carrier-directory/CarrierNameSelect';
import { useCreateCoverageMutation } from '@/services/api/coverageService';
import {
  BenefitType,
  CoverageCategory,
  IdentifierType,
  PolicyType,
} from '@/services/types/coverage-types';
import { toTitleCase } from '@/lib/strings';

import {
  FormField,
  FormGroup,
  FormInput,
  FormLabel,
  FormSelect,
} from './form';

// ── Types ────────────────────────────────────────────────────────────────────

export interface CoverageFormData {
  carrierName: string;
  carrierId: string | null;
  divisionId: string | null;
  policyType: PolicyType | '';
  policyNumber: string;
  claimNumber: string;
  adjusterName: string;
  adjusterPhone: string;
  adjusterEmail: string;
  adjusterFax: string;
  namedInsured: string;
  benefitType: BenefitType | '';
  policyLimit: string;
}

export const EMPTY_COVERAGE: CoverageFormData = {
  carrierName: '',
  carrierId: null,
  divisionId: null,
  policyType: '',
  policyNumber: '',
  claimNumber: '',
  adjusterName: '',
  adjusterPhone: '',
  adjusterEmail: '',
  adjusterFax: '',
  namedInsured: '',
  benefitType: '',
  policyLimit: '',
};

interface CoverageEntryFormProps {
  data: CoverageFormData;
  isImported: boolean;
  isEditing?: boolean;
  index: number;
  matterId: string;
  category: CoverageCategory;
  onAdded: () => void;
  onRemove: () => void;
  /** For edit mode: called with the built payload to PATCH the existing coverage */
  onUpdate?: (payload: Record<string, unknown>) => Promise<void>;
}

// ── Component ────────────────────────────────────────────────────────────────

export const CoverageEntryForm: React.FC<CoverageEntryFormProps> = ({
  data: initialData,
  isImported,
  isEditing = false,
  index,
  matterId,
  category,
  onUpdate,
  onAdded,
  onRemove,
}) => {
  const [form, setForm] = useState<CoverageFormData>(initialData);
  const [isAdding, setIsAdding] = useState(false);
  const [createCoverage] = useCreateCoverageMutation();

  const update = (field: keyof CoverageFormData, value: string | null) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleCarrierChange = useCallback((name: string, carrierId?: string | null) => {
    setForm((f) => ({ ...f, carrierName: name, carrierId: carrierId ?? null }));
  }, []);

  const handleCarrierDivision = useCallback((carrierId: string, divisionId?: string | null) => {
    setForm((f) => ({ ...f, carrierId, divisionId: divisionId ?? null }));
  }, []);

  const buildPayload = useCallback(() => {
    const adjusterHandles: Array<{ handle_type: string; value: string }> = [];
    if (form.adjusterPhone?.trim()) adjusterHandles.push({ handle_type: 'PHONE', value: form.adjusterPhone.trim() });
    if (form.adjusterEmail?.trim()) adjusterHandles.push({ handle_type: 'EMAIL', value: form.adjusterEmail.trim() });
    if (form.adjusterFax?.trim()) adjusterHandles.push({ handle_type: 'FAX', value: form.adjusterFax.trim() });

    const hasAdjuster = form.adjusterName?.trim() || adjusterHandles.length > 0;

    const benefits = [];
    if (form.benefitType && form.policyLimit) {
      const limitNum = parseFloat(form.policyLimit.replace(/[^0-9.]/g, ''));
      if (!Number.isNaN(limitNum)) {
        benefits.push({ benefit_type: form.benefitType, limitations: { limit: limitNum }, is_stacked: null });
      }
    }

    return {
      category,
      policy_type: (form.policyType as PolicyType) || PolicyType.AUTO,
      carrier: { name: form.carrierName, handles: null, address: null },
      carrier_v2_id: form.carrierId || null,
      carrier_division_id: form.divisionId || null,
      policy_identifiers: form.policyNumber
        ? [{ identifier_type: IdentifierType.POLICY_NUMBER, identifier_value: form.policyNumber }]
        : null,
      named_insured: form.namedInsured?.trim() ? [form.namedInsured.trim()] : null,
      benefits: benefits.length > 0 ? benefits : null,
      adjusters: hasAdjuster
        ? [{ benefit_type: (form.benefitType as BenefitType) || null, name: form.adjusterName?.trim() || null, handles: adjusterHandles.length > 0 ? adjusterHandles : null }]
        : null,
      claims: form.claimNumber
        ? [{ benefit_type: (form.benefitType as BenefitType) || null, claim_identifier: form.claimNumber }]
        : null,
      vehicles: null,
      policy_details: null,
      subrogator: null,
    };
  }, [form, category]);

  const handleAdd = useCallback(async () => {
    if (!form.carrierName.trim()) {
      toast.error('Carrier name is required');
      return;
    }

    setIsAdding(true);
    try {
      if (isEditing && onUpdate) {
        // PATCH existing coverage
        await onUpdate(buildPayload());
        toast.success(`Updated ${form.carrierName}`);
      } else {
        // Create new coverage
        await createCoverage({
          matterId,
          coverage: buildPayload(),
        }).unwrap();
        toast.success(`Added ${form.carrierName}`);
      }
      onAdded();
    } catch {
      toast.error(isEditing ? 'Failed to update coverage' : 'Failed to add coverage');
    } finally {
      setIsAdding(false);
    }
  }, [form, matterId, isEditing, onUpdate, buildPayload, createCoverage, onAdded]);

  const canAdd = !!form.carrierName.trim() && !isAdding;

  return (
    <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-zinc-50 border-b border-zinc-100">
        <span className="text-xs font-semibold text-zinc-600">
          {isImported && <Sparkles className="inline h-3 w-3 text-blue-500 mr-1 -mt-0.5" />}
          {isEditing ? 'Edit Coverage' : `Coverage ${index + 1}`}
        </span>
        <button
          type="button"
          onClick={onRemove}
          className="rounded p-1 text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="px-4 py-3 space-y-4">
        <FormGroup>
          {/* Carrier */}
          <FormField assisted={isImported && !!initialData.carrierName && form.carrierName === initialData.carrierName}>
            <FormLabel required>Carrier</FormLabel>
            <div data-slot="control">
              <CarrierNameSelect
                value={form.carrierName || null}
                onChange={handleCarrierChange}
                onSelectDirectory={handleCarrierDivision}
                disabled={isAdding}
                placeholder="Search carriers..."
                className="w-full justify-between"
              />
            </div>
          </FormField>

          {/* Policy details */}
          <div className="grid grid-cols-2 gap-3">
            <FormField>
              <FormLabel>Policy Type</FormLabel>
              <FormSelect
                value={form.policyType}
                onChange={(e) => update('policyType', e.target.value)}
                placeholder="Select"
                disabled={isAdding}
              >
                {Object.values(PolicyType).map((t) => (
                  <option key={t} value={t}>{toTitleCase(t.replace(/_/g, ' '))}</option>
                ))}
              </FormSelect>
            </FormField>
            <FormField assisted={isImported && !!initialData.policyNumber && form.policyNumber === initialData.policyNumber}>
              <FormLabel>Policy Number</FormLabel>
              <FormInput
                value={form.policyNumber}
                onChange={(e) => update('policyNumber', e.target.value)}
                placeholder="e.g., SF-123456"
                disabled={isAdding}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField assisted={isImported && !!initialData.claimNumber && form.claimNumber === initialData.claimNumber}>
              <FormLabel>Claim Number</FormLabel>
              <FormInput
                value={form.claimNumber}
                onChange={(e) => update('claimNumber', e.target.value)}
                placeholder="e.g., CL-789"
                disabled={isAdding}
              />
            </FormField>
            <FormField>
              <FormLabel>Named Insured</FormLabel>
              <FormInput
                value={form.namedInsured}
                onChange={(e) => update('namedInsured', e.target.value)}
                placeholder="Policy holder name"
                disabled={isAdding}
              />
            </FormField>
          </div>

          {/* Adjuster */}
          <div className="grid grid-cols-2 gap-3">
            <FormField assisted={isImported && !!initialData.adjusterName && form.adjusterName === initialData.adjusterName}>
              <FormLabel>Adjuster Name</FormLabel>
              <FormInput
                value={form.adjusterName}
                onChange={(e) => update('adjusterName', e.target.value)}
                placeholder="Adjuster name"
                disabled={isAdding}
              />
            </FormField>
            <FormField assisted={isImported && !!initialData.adjusterPhone && form.adjusterPhone === initialData.adjusterPhone}>
              <FormLabel>Adjuster Phone</FormLabel>
              <FormInput
                value={form.adjusterPhone}
                onChange={(e) => update('adjusterPhone', e.target.value)}
                placeholder="(555) 123-4567"
                disabled={isAdding}
              />
            </FormField>
          </div>

          {/* Benefit */}
          <div className="grid grid-cols-2 gap-3">
            <FormField>
              <FormLabel>Benefit Type</FormLabel>
              <FormSelect
                value={form.benefitType}
                onChange={(e) => update('benefitType', e.target.value)}
                placeholder="Select"
                disabled={isAdding}
              >
                {Object.values(BenefitType).map((t) => (
                  <option key={t} value={t}>{toTitleCase(t.replace(/_/g, ' '))}</option>
                ))}
              </FormSelect>
            </FormField>
            <FormField>
              <FormLabel>Policy Limit</FormLabel>
              <FormInput
                value={form.policyLimit}
                onChange={(e) => update('policyLimit', e.target.value)}
                placeholder="$25,000"
                disabled={isAdding}
              />
            </FormField>
          </div>
        </FormGroup>

        <button
          type="button"
          onClick={handleAdd}
          disabled={!canAdd}
          className={
            'w-full flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold ' +
            'bg-blue-600 text-white hover:bg-blue-700 ' +
            'disabled:opacity-50 disabled:cursor-not-allowed ' +
            'transition-colors duration-150'
          }
        >
          {isAdding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isEditing ? 'Save Changes' : 'Add Coverage'}
        </button>
      </div>
    </div>
  );
};
